import React, { useState, useRef, useEffect } from 'react';
import { Zap, X } from 'lucide-react';

/**
 * QuickReplyButton Component
 * Provides predefined message templates for faster communication
 */
const QuickReplyButton = ({ onSelect, userRole = 'gigger' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const templates = {
        common: [
            "Hi there! 👋",
            "Thank you!",
            "Sounds good to me.",
            "Could you please clarify?",
            "I'll get back to you shortly."
        ],
        gigger: [
            "I can definitely help with that!",
            "When do you need this delivered?",
            "Please check my custom offer above.",
            "I've started working on your order.",
            "Could you provide more details/requirements?"
        ],
        shipper: [
            "Are you available for a custom job?",
            "What is your estimated delivery time?",
            "Can you share some recent examples?",
            "I'd like to request a revision.",
            "Just placed the order! Looking forward to it."
        ]
    };

    const getTemplates = () => {
        const roleTemplates = userRole === 'shipper' ? templates.shipper : templates.gigger;
        return [...templates.common, ...roleTemplates];
    };

    const handleSelect = (text) => {
        onSelect(text);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={popoverRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`p-2 rounded-full transition-colors ${isOpen ? 'bg-yellow-100 text-yellow-600' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                title="Quick Replies"
            >
                <Zap className="w-5 h-5" />
            </button>

            {isOpen && (
                <div className="absolute bottom-12 left-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-bottom-2">
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Quick Replies
                        </span>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </div>

                    <div className="max-h-60 overflow-y-auto p-2 space-y-1">
                        {getTemplates().map((text, index) => (
                            <button
                                key={index}
                                onClick={() => handleSelect(text)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded transition-colors truncate"
                                title={text}
                            >
                                {text}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickReplyButton;
